import * as amplitude from '@amplitude/analytics-browser';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { AppStore } from '../AppStore';
import { ArithmeglyphsStore } from '../components/_puzzles/Arithmeglyphs/ArithmeglyphsStore';
import { MegarubigramStore } from '../components/_puzzles/Megarubigram/MegarubigramStore';
import { MorphologyStore } from '../components/_puzzles/Morphology/MorphologyStore';
import { PathwordStore } from '../components/_puzzles/Pathword/PathwordStore';
import { RubigramStore } from '../components/_puzzles/Rubigram/RubigramStore';
import { TriwordStore } from '../components/_puzzles/Triword/TriwordStore';
import { VaryCloveStore } from '../components/_puzzles/VaryClove/VaryCloveStore';
import { PuzzleNames, PuzzleState, UpdateProgressBody } from './types';
import { Pathword5x5Store } from '../components/_puzzles/Pathword5x5/Pathword5x5Store';
import { resetPathwordData } from '../components/_puzzles/Pathword/pathwordUtils';
import { resetPathword5x5Data } from '../components/_puzzles/Pathword5x5/pathword5x5utils';
import { TruthsortingStore } from '../components/_puzzles/Truthsorting/TruthsortingStore';
import { UltrarubigramStore } from '../components/_puzzles/Ultrarubigram/UltrarubigramStore';
import { applySavedOrientations, resetOrientations } from '../components/_puzzles/Ultrarubigram/geometry';

export const savePuzzleProgress = async (puzzleName: PuzzleNames, puzzleState: PuzzleState) => {
  try {
    if (puzzleName === PuzzleNames.Morphology || puzzleName === PuzzleNames.Arithmeglyphs) {
      if (puzzleState.completed) {
        amplitude.track(`${puzzleName}_completed`);
        if (puzzleState.revealed) {
          amplitude.track(`${puzzleName}_revealed`);
        }
      }
    } else {
      if (puzzleState.solved) {
        amplitude.track(`${puzzleName}_solved`);
      } else if (puzzleState.revealed) {
        amplitude.track(`${puzzleName}_revealed`);
      }
    }
  } catch (error) { /* just in case their lib has some issue */ }

  // Track puzzle solve in backend analytics (for both logged-in and logged-out users)
  if (puzzleState.solved) {
    try {
      await axios.post('/api/analytics/track-puzzle-solve', {
        puzzleName,
        source: 'web',
      });
    } catch (error) {
      // Fail silently - analytics shouldn't break the app
    }
  }

  const auth = getAuth();
  const authToken = await auth.currentUser?.getIdToken(true);

  if (authToken) {
    // we're logged in, so send update to server to save in mongo
    try {
      const { data: newStats } = await axios.post<any>(
        '/api/users/update-progress', {
        authToken,
        puzzleName,
        puzzleState,
      } as UpdateProgressBody);

      AppStore.update(s => {
        s.userData!.stats[puzzleName] = newStats;
      });
    } catch (error) {
      // unable to save
    }
  } else {
    // we're not logged in, so just save in localStorage
    const localStorage = window.localStorage;
    localStorage.setItem(puzzleName, JSON.stringify(puzzleState));
  }
};

// If the id matches, we load the data for the puzzle. If not, we clear it and return null,
// and then the code further below will reset the stores instead.
export const loadPuzzleState = (
  puzzleName: PuzzleNames,
  puzzleIds: { [puzzleName: string]: number }
): PuzzleState | null => {
  const raw = localStorage.getItem(puzzleName);
  const parsed = JSON.parse(raw ?? '{}');
  // temporary while truthsortingV1 is being deprecated; full removal pending
  puzzleIds[PuzzleNames.Truthsorting] = puzzleIds['truthsortingV2'];
  if (parsed.puzzleId !== puzzleIds[puzzleName]) {
    localStorage.removeItem(puzzleName);
    return null;
  }
  return parsed;
};

// used for creating new account and saving any progress for that day to the new account
export const loadAllPuzzleStates = () => {
  const allStates: any = {};
  Object.values(PuzzleNames)
    .forEach(puzzleName => {
      const raw = localStorage.getItem(puzzleName);
      const parsed = JSON.parse(raw ?? '{}');
      allStates[puzzleName] = parsed;
    });
  return allStates;
};

// On logged-out page load, for each puzzle this either loads any existing logged-out 
// progress for that puzzle, or otherwise clears the localstorage if the saved data is 
// for an older puzzle.
export const loadOrResetLocalStorage = (puzzleIds: {[puzzleName: string]: number}) => {
  ArithmeglyphsStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Arithmeglyphs, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    s.completed = false;
    
    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
      s.completed = saved.completed || s.completed;

      // If there was no saved state it's fine because initArithmeglyphs sets this
      // up with the correct defaults to begin with, and that would be called
      // when the data is loaded or reloaded on page load or logout.
      if (saved.partialState?.dataByLine) {
        const d = saved.partialState.dataByLine;
        s.dataByLine.forEach((line, i) => {
          line.guesses = d[i].guesses || line.guesses;
          line.revealedCells = d[i].revealedCells || line.revealedCells;
        });
      }
    }
  });

  UltrarubigramStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Ultrarubigram, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    s.rot = 0;
    s.isRevealing = false;
    resetOrientations(s.grids);

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
      if (saved.partialState && applySavedOrientations(s.grids, saved.partialState.grids)) {
        s.rot = saved.partialState.rot || 0;
      }
    }
  });

  MegarubigramStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Megarubigram, puzzleIds);
    
    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    s.degreesRotated = 0;

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
      s.degreesRotated = saved.partialState?.degreesRotated || s.degreesRotated;
      s.grids = saved.partialState?.grids || s.grids;
    }
  });

  MorphologyStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Morphology, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    s.completed = false;
    s.guesses = s.clues.map(
      (clue) => Array(clue.solution.length).fill(null)
    );
    s.revealedCells = s.clues.map(
      (clue) => Array(clue.solution.length).fill(false)
    );
    s.mistakes = s.clues.map(
      (clue) => Array(clue.solution.length).fill(false)
    );

    // If a saved state exists, override defaults with saved data
    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
      s.completed = saved.completed || s.completed;
      s.guesses = saved.partialState?.guesses || s.guesses;
      s.revealedCells = saved.partialState?.revealedCells || s.revealedCells;
      s.mistakes = saved.partialState?.mistakes || s.mistakes;
    }
  });

  PathwordStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Pathword, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    resetPathwordData(s);

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
    }
  });

  Pathword5x5Store.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Pathword5x5, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    resetPathword5x5Data(s);

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
    }
  });

  RubigramStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Rubigram, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
    }
  });

  TriwordStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Triword, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    s.used5050Lifeline = false;
    s.usedLetterLifeline = false;
    s.usedPositionLifeline = false;

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
      s.used5050Lifeline = saved.partialState?.used5050Lifeline
        || s.used5050Lifeline;
      s.usedLetterLifeline = saved.partialState?.usedLetterLifeline
        || s.usedLetterLifeline;
      s.usedPositionLifeline = saved.partialState?.usedPositionLifeline
        || s.usedPositionLifeline;
    }
  });

  TruthsortingStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.Truthsorting, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
    }
  });

  VaryCloveStore.update(s => {
    const saved = loadPuzzleState(PuzzleNames.VaryClove, puzzleIds);

    // Set default values
    s.time = 0;
    s.solved = false;
    s.revealed = false;

    if (saved) {
      s.time = saved.time || s.time;
      s.solved = saved.solved || s.solved;
      s.revealed = saved.revealed || s.revealed;
    }
  });
};

// ---------------------------------------------------------------------------
// Archive Morphology local persistence
//
// The archive (permalink) Morphology pages never use savePuzzleProgress / the
// daily streak+stats flow. Instead they persist in-progress state to localStorage
// keyed by the puzzle's own id, so any number of past puzzles can be resumed
// independently. This applies to BOTH signed-in and signed-out users (it is
// purely local), and is cleared when the puzzle is completed.
// ---------------------------------------------------------------------------

export type ArchiveMorphologyLocalState = {
  puzzleId: number;
  time: number;
  solved: boolean;
  revealed: boolean;
  completed: boolean;
  guesses: (string | null)[][];
  revealedCells: boolean[][];
  mistakes: boolean[][];
};

const archiveMorphologyKey = (puzzleId: number) => `morphologyArchive-${puzzleId}`;

export const saveArchiveMorphologyState = (state: ArchiveMorphologyLocalState) => {
  try {
    localStorage.setItem(archiveMorphologyKey(state.puzzleId), JSON.stringify(state));
  } catch (error) {
    // localStorage may be full or unavailable; saving progress is best-effort.
  }
};

// Returns the saved state for this puzzleId, or null if none (or if a stale/
// mismatched entry is found, in which case it is cleaned up).
export const loadArchiveMorphologyState = (
  puzzleId: number
): ArchiveMorphologyLocalState | null => {
  const raw = localStorage.getItem(archiveMorphologyKey(puzzleId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ArchiveMorphologyLocalState;
  } catch (error) {
    localStorage.removeItem(archiveMorphologyKey(puzzleId));
    return null;
  }
};

export const clearArchiveMorphologyState = (puzzleId: number) => {
  localStorage.removeItem(archiveMorphologyKey(puzzleId));
};